import { Page, Document, Text, View } from '@react-pdf/renderer';
import { styles, PageHeader, PageHeaderLogo, PageFooter } from './constants.js';
import { getLocalTime, getPdfTimeSetting } from '../time.js';
import { useTranslation } from 'react-i18next';
import { keys } from '../translator/translation_keys.js';
import { vehicleString } from '../strings.js';
import {
  modelLookUp,
  statusLookup,
  groupLookUp,
  formatFilterString,
  roundFraction,
} from '../../pages/analytics/count_analytics/report/Reports.utils.js';
import { defaultAssessmentTypes } from '../../constants/strings.js';

export const ReportDocument = ({ report, company, props }) => {
  const { t, ready } = useTranslation();
  const isMilitary = getPdfTimeSetting() === 'military';
  const {
    assessmentName,
    incidentName,
    observationName,
    allUsers,
    allWorkspaces,
    activeTemplateTypes,
    allVehicles,
    activeTemplates,
  } = company;
  const { options, data } = report;
  const { showAllUsers, showPercent, showAllWorkspaces } = props;

  const userKeys = ['userId', 'participants', 'assigneeId', 'creatorId'];
  const getAllWorkspaces = (groups) => {
    const workspacesWithValues = [];

    groups?.workspaceId?.forEach(({ value }) =>
      workspacesWithValues.push(value),
    );

    const remainingWorkspaces = allWorkspaces.filter(
      ({ id }) => !workspacesWithValues.includes(id),
    );

    return remainingWorkspaces;
  };

  const getAllUsers = (groups, userKey) => {
    const usersWithValues = [];

    groups?.[userKey]?.forEach(({ value }) => usersWithValues.push(value));

    const remainingUsers = allUsers.filter(
      ({ id }) => !usersWithValues.includes(id),
    );

    const groupKey = userKey === 'participants' ? 'userId' : userKey;

    return { users: remainingUsers, groupKey };
  };

  const GroupRow = ({
    row: { count = 0, key: groupKey, value = '', target = null, total = 0 },
  }) => {
    let content = '';

    switch (groupKey) {
      case 'workspaceId':
        content =
          allWorkspaces.find((w) => w.id === value)?.title ??
          t(keys.common.NONE);
        break;
      case 'assigneeId':
      case 'creatorId':
      case 'userId':
        content =
          allUsers.find((w) => w.id === value)?.firstName ??
          t(keys.common.NONE);
        break;
      case 'vehicleId':
        const asset = allVehicles.find((v) => v.id === value);
        content = !!asset
          ? vehicleString(asset)
          : !asset && !value
          ? t(keys.common.NONE)
          : t(keys.report.ARCHIVED);
        break;
      case 'inspectionType':
        const targetType =
          activeTemplateTypes[value.toLowerCase()] ??
          defaultAssessmentTypes[
            value === 'VEHICLE' ? 'fleet' : value.toLowerCase()
          ];
        content = targetType?.name ?? value;
        break;
      case 'dateTime':
      case 'dateOfIncident':
        content = !!value
          ? getLocalTime(value).format(
              isMilitary ? 'MMM D YYYY - HH:mm' : 'MMM D YYYY - h:mma',
            )
          : t(keys.common.NONE);
        break;
      case 'isDraft':
        content = value ? t(keys.common.COMPLETE) : t(keys.common.IN_PROGRESS);
        break;
      case 'status':
        content = t(statusLookup[value]?.title) ?? t(keys.common.NONE);
        break;
      case 'auditType':
      case 'type':
      case 'subtype':
      case 'location':
        content = value ?? t(keys.common.NONE);
        break;
      case 'lostTime':
      case 'nearMiss':
        content =
          value === null
            ? t(keys.common.NONE)
            : value === true
            ? t(keys.common.YES)
            : t(keys.common.NO);
        break;
      case 'passed':
        content = value ? t(keys.assessments.PASS) : t(keys.assessments.FAIL);
        break;
      default:
        content = '';
        break;
    }

    return (
      <View style={styles.row}>
        <Text style={styles.text}>{content}</Text>
        <View style={styles.flexRow}>
          {showPercent ? (
            <View style={styles.flexRowGapSm}>
              <Text style={styles.text}>{roundFraction(count / total)}%</Text>
              {target && (
                <Text style={styles.text}>
                  {roundFraction(count / target)}%
                </Text>
              )}
            </View>
          ) : (
            <View style={styles.flexRowGapSm}>
              <Text style={styles.text}>
                {count}/{total}
              </Text>
              {target && (
                <Text style={styles.text}>
                  {count}/{target}
                </Text>
              )}
            </View>
          )}
        </View>
      </View>
    );
  };

  return (
    report &&
    data &&
    ready && (
      <Document>
        <Page size="A4" style={styles.page} wrap>
          <PageHeaderLogo company={company} />
          <PageHeader
            company={company}
            data={report}
            title={report.title}
            type={t(keys.common.REPORT)}
          />
          {[...options]
            ?.sort((a, b) => a.order - b.order)
            ?.map((o, i) => (
              <View key={`main-group-${o?.key}-${i}`}>
                <View style={styles.tableRow}>
                  <Text style={styles.titleHeader}>
                    {modelLookUp({
                      model: o.refModel !== o.model ? o.refModel : o.model,
                      t,
                      companyNames: {
                        assessmentName,
                        incidentName,
                        observationName,
                      },
                    })}
                  </Text>
                  <Text style={styles.titleHeader}>
                    {t(keys.common.TOTAL, {
                      variable: data?.[o.key]?.total ?? 0,
                    })}
                  </Text>
                </View>
                {[...o?.filters]?.filter((f) => !f.default)?.length > 0 && (
                  <Text style={styles.title}>
                    {formatFilterString({
                      ...[...o?.filters]?.filter((f) => !f.default)[0],
                      types: activeTemplateTypes,
                      templates: activeTemplates,
                      t,
                    })}
                  </Text>
                )}
                <View style={styles.groupContainer}>
                  {o?.groups?.map((g, i) => (
                    <View key={`pdf-group-${o?.key}-${i}`}>
                      <View style={styles.row}>
                        <Text style={styles.titleHeader}>
                          {groupLookUp(g?.by, t)}
                        </Text>
                        <View
                          style={{
                            ...styles.titleHeader,
                            ...styles.flexRowGapSm,
                          }}
                        >
                          <Text style={styles.text}>
                            {t(keys.common.TOTAL, {
                              variable: null,
                            })}
                          </Text>
                          {!!g?.target && (
                            <Text style={styles.text}>
                              {t(keys.report.TARGET, { variable: null })}
                            </Text>
                          )}
                        </View>
                      </View>
                      {[...data?.[o.key]?.groups?.[g.by]]
                        ?.sort((a, b) => b.count - a.count)
                        ?.map((r, i) => (
                          <GroupRow
                            key={`row-${o?.key}-${i}`}
                            row={{
                              ...r,
                              target: g?.target,
                              total: data?.[o.key]?.total,
                            }}
                          />
                        )) ?? 0}
                      {g.by === 'workspaceId' &&
                        showAllWorkspaces &&
                        getAllWorkspaces(data?.[o.key]?.groups).map((w, i) => (
                          <GroupRow
                            key={`pdf-allWorkspaces-${o?.key}-${i}`}
                            row={{
                              count: 0,
                              key: 'workspaceId',
                              value: w?.id,
                              target: g?.target,
                              total: data?.[o.key]?.total,
                            }}
                          />
                        ))}
                      {userKeys.includes(g.by) &&
                        showAllUsers &&
                        getAllUsers(data?.[o.key]?.groups, g.by)?.users?.map(
                          (w, i) => (
                            <GroupRow
                              key={`pdf-allUsers-${o?.key}-${i}`}
                              row={{
                                count: 0,
                                key: getAllUsers(data?.[o.key]?.groups, g.by)
                                  ?.groupKey,
                                value: w?.id,
                                target: g?.target,
                                total: data?.[o.key]?.total,
                              }}
                            />
                          ),
                        )}
                    </View>
                  ))}
                </View>
              </View>
            ))}
          <PageFooter />
        </Page>
      </Document>
    )
  );
};
